import { Result, Image } from './types';

export interface ProcessedImageData {
    rowNumber: number;
    name: string;
    status: string;
    qualityGood: number;
    qualityPoor: number;
    tumor: number;
    stroma: number;
    necrosis: number;
    lymphocyte: number;
    background: number;
    backgroundMucin: number;
    qualityResult?: Result;
    segmentationResult?: Result;
}

export const processImageData = (results: Result[], image: Image, index: number): ProcessedImageData => {
    // Handle case where image is undefined
    if (!image) {
        return {
            rowNumber: index + 1,
            name: 'Unknown Image',
            status: 'Error',
            qualityGood: 0,
            qualityPoor: 0,
            tumor: 0,
            stroma: 0,
            necrosis: 0,
            lymphocyte: 0,
            background: 0,
            backgroundMucin: 0
        };
    }

    // Sort the results in descending order by the updated_at property
    const sortedResults = [...results].sort((a, b) => 
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );

    // Find the latest quality and segmentation results
    const qualityResult = sortedResults.find(result => 
        result.model_path.includes('processing-quality-classification')
    );
    const segmentationResult = sortedResults.find(result => 
        result.model_path.includes('semantic')
    );

    const getQualityMeasurement = (key: string) => {
        const measurement = qualityResult?.result_data?.[0]?.['AI-Measurement']?.['withoutBackground']?.[key];
        if (typeof measurement !== 'number') {
            return 0;
        }
        return measurement.toFixed(2);
    };

    const getSegmentationMeasurement = (key: string) => {
        const measurement = segmentationResult?.result_data?.[0]?.['AI-Measurement']?.['withoutBackground']?.[key];
        if (typeof measurement !== 'number') {
            return 0;
        }
        return measurement.toFixed(2)
    };

    return {
        rowNumber: index + 1,
        name: image.name,
        status: qualityResult?.status || segmentationResult?.status || 'Not Complete',
        qualityGood: Number(getQualityMeasurement('GoodPercentage')),
        qualityPoor: Number(getQualityMeasurement('PoorPercentage')), 
        tumor: Number(getSegmentationMeasurement('TumorPercentage')),
        stroma: Number(getSegmentationMeasurement('StromaPercentage')),
        necrosis: Number(getSegmentationMeasurement('NecrosisPercentage')),
        lymphocyte: Number(getSegmentationMeasurement('LymphocyteInfiltrateClusterPercentage')),
        background: Number(getSegmentationMeasurement('BackgroundPercentage')),
        backgroundMucin: Number(getSegmentationMeasurement('BackgroundMucinPercentage')),
        qualityResult,
        segmentationResult
    };
};

export const sortProcessedData = (
    data: ProcessedImageData[],
    sortField: keyof ProcessedImageData,
    sortDirection: 'asc' | 'desc' | null
): ProcessedImageData[] => {
    if (!sortDirection) return data;

    return [...data].sort((a, b) => {
        const aValue = a[sortField];
        const bValue = b[sortField];

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortDirection === 'asc' 
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        }

        return sortDirection === 'asc'
            ? Number(aValue) - Number(bValue)
            : Number(bValue) - Number(aValue);
    });
}; 